import React, { useState, useEffect, useRef } from 'react';
import {
    Bell,
    X,
    Check,
    CheckCheck,
    Trash2,
    Share2,
    FileText,
    Folder,
    Info,
    AlertTriangle,
    AlertCircle,
    CheckCircle,
    ExternalLink,
    Clock
} from 'lucide-react';
import '../styles/NotificationInbox.css';

const NotificationInbox = ({ fileApiRequest, onNavigate, onViewAll }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const dropdownRef = useRef(null);

    // Fetch notifications
    const fetchNotifications = async () => {
        try {
            setLoading(true);
            const response = await fileApiRequest('/notifications?limit=20');
            setNotifications(response.notifications || []);
        } catch (err) {
            console.error('Failed to fetch notifications:', err);
        } finally {
            setLoading(false);
        }
    };

    // Fetch unread count
    const fetchUnreadCount = async () => {
        try {
            const response = await fileApiRequest('/notifications/count');
            setUnreadCount(response.count || 0);
        } catch (err) {
            console.error('Failed to fetch notification count:', err);
        }
    };

    // Mark notification as read
    const markAsRead = async (notificationId) => {
        try {
            await fileApiRequest(`/notifications/${notificationId}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_read: true })
            });
            setNotifications(prev =>
                prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Failed to mark as read:', err);
        }
    };

    // Dismiss notification
    const dismissNotification = async (notificationId, e) => {
        e.stopPropagation();
        try {
            await fileApiRequest(`/notifications/${notificationId}`, {
                method: 'PATCH',
                body: JSON.stringify({ is_dismissed: true })
            });
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            // Decrease unread count if the dismissed notification was unread
            const notification = notifications.find(n => n.id === notificationId);
            if (notification && !notification.is_read) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Failed to dismiss notification:', err);
        }
    };

    // Mark all as read
    const markAllAsRead = async () => {
        try {
            await fileApiRequest('/notifications/mark-all-read', { method: 'POST' });
            setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to mark all as read:', err);
        }
    };

    // Dismiss all
    const dismissAll = async () => {
        try {
            await fileApiRequest('/notifications/dismiss-all', { method: 'POST' });
            setNotifications([]);
            setUnreadCount(0);
        } catch (err) {
            console.error('Failed to dismiss all:', err);
        }
    };

    // Handle notification click
    const handleNotificationClick = (notification) => {
        if (!notification.is_read) {
            markAsRead(notification.id);
        }

        if (notification.action_url && onNavigate) {
            onNavigate(notification.action_url);
            setIsOpen(false);
        }
    };

    // Get icon based on notification type/category
    const getNotificationIcon = (notification) => {
        const { type, category } = notification;

        if (category === 'file_share') {
            return <Share2 className="notif-icon share" />;
        }

        switch (type) {
            case 'success':
                return <CheckCircle className="notif-icon success" />;
            case 'warning':
                return <AlertTriangle className="notif-icon warning" />;
            case 'error':
                return <AlertCircle className="notif-icon error" />;
            default:
                return <Info className="notif-icon info" />;
        }
    };

    // Format relative time
    const formatRelativeTime = (dateString) => {
        if (!dateString) return '';

        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        if (diffHours < 24) return `${diffHours}h ago`;
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Fetch data on mount and periodically
    useEffect(() => {
        fetchUnreadCount();

        // Poll for new notifications every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, []);

    // Fetch full notifications when dropdown opens
    useEffect(() => {
        if (isOpen) {
            fetchNotifications();
        }
    }, [isOpen]);

    return (
        <div className="notification-inbox" ref={dropdownRef}>
            <button
                className="notification-bell-btn"
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Notifications"
            >
                <Bell className="bell-icon" />
                {unreadCount > 0 && (
                    <span className="notification-badge">
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                )}
            </button>

            {isOpen && (
                <div className="notification-dropdown">
                    <div className="notification-header">
                        <h3>Notifications</h3>
                        <div className="notification-actions">
                            {unreadCount > 0 && (
                                <button
                                    className="action-btn"
                                    onClick={markAllAsRead}
                                    title="Mark all as read"
                                >
                                    <CheckCheck size={16} />
                                </button>
                            )}
                            {notifications.length > 0 && (
                                <button
                                    className="action-btn"
                                    onClick={dismissAll}
                                    title="Dismiss all"
                                >
                                    <Trash2 size={16} />
                                </button>
                            )}
                            <button
                                className="action-btn close-btn"
                                onClick={() => setIsOpen(false)}
                            >
                                <X size={16} />
                            </button>
                        </div>
                    </div>

                    <div className="notification-list">
                        {loading ? (
                            <div className="notification-loading">
                                <div className="loading-spinner-small"></div>
                                <span>Loading...</span>
                            </div>
                        ) : notifications.length === 0 ? (
                            <div className="notification-empty">
                                <Bell className="empty-icon" />
                                <p>No notifications</p>
                                <span>You're all caught up!</span>
                            </div>
                        ) : (
                            notifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.is_read ? 'unread' : ''} ${notification.type}`}
                                    onClick={() => handleNotificationClick(notification)}
                                >
                                    <div className="notification-icon-wrapper">
                                        {getNotificationIcon(notification)}
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-title">{notification.title}</div>
                                        <div className="notification-message">{notification.message}</div>
                                        <div className="notification-meta">
                                            <span className="notification-time">
                                                <Clock size={12} />
                                                {formatRelativeTime(notification.created_at)}
                                            </span>
                                            {notification.source === 'timefiles' && (
                                                <span className="notification-source">TimeFiles</span>
                                            )}
                                        </div>
                                        {notification.action_text && (
                                            <button className="notification-action-link">
                                                {notification.action_text}
                                                <ExternalLink size={12} />
                                            </button>
                                        )}
                                    </div>
                                    {notification.is_dismissable && (
                                        <button
                                            className="dismiss-btn"
                                            onClick={(e) => dismissNotification(notification.id, e)}
                                            title="Dismiss"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                    {!notification.is_read && (
                                        <div className="unread-indicator"></div>
                                    )}
                                </div>
                            ))
                        )}
                    </div>

                    {/* View All Footer */}
                    {notifications.length > 0 && onViewAll && (
                        <div className="notification-footer">
                            <button
                                className="view-all-btn"
                                onClick={() => {
                                    setIsOpen(false);
                                    onViewAll();
                                }}
                            >
                                View all notifications
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationInbox;
